import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import './SchoolDashboard.css';
import './PickupLocations.css';
import { useBranchFilter } from '../../hooks/useBranchFilter';

function PickupLocations() {
  const navigate = useNavigate();
  const [schoolData, setSchoolData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { selectedBranchId, handleBranchChange, refreshTrigger } = useBranchFilter();
  const [pickupLocations, setPickupLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [viewingLocation, setViewingLocation] = useState(null);
  const [editingLocation, setEditingLocation] = useState(null);
  const [editFormData, setEditFormData] = useState({
    location_name: '',
    address: '',
    latitude: '',
    longitude: '',
    contact_person: '',
    contact_phone: '',
    notes: '',
    price_one_way: '',
    price_two_way: '',
    currency: 'USD',
    is_active: true
  });
  const [editErrors, setEditErrors] = useState({});
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    const sd = localStorage.getItem('schoolData');
    if (!sd) {
      navigate('/schoollogin');
      return;
    }
    setSchoolData(JSON.parse(sd));
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (schoolData?.id) {
      fetchPickupLocations();
    }
  }, [schoolData?.id, selectedBranchId, refreshTrigger]);

  useEffect(() => {
    if (selectedBranchId !== undefined) {
      setPickupLocations([]);
    }
  }, [selectedBranchId]);

  const fetchPickupLocations = async () => {
    try {
      setLoadingLocations(true);
      let query = supabase
        .from('pickup_locations')
        .select('*')
        .eq('school_id', schoolData.id)
        .order('created_at', { ascending: false });

      if (selectedBranchId != null) {
        query = query.eq('branch_id', selectedBranchId);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching pickup locations:', error);
        alert('Error loading pickup locations: ' + error.message);
        return;
      }

      setPickupLocations(data || []);
    } catch (err) {
      console.error('Error:', err);
      alert('An error occurred while loading pickup locations.');
    } finally {
      setLoadingLocations(false);
    }
  };

  const handleLogout = () => navigate('/schoollogin');

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleViewLocation = (location) => {
    setViewingLocation(location);
  };

  const handleEditLocation = (location) => {
    setEditingLocation(location);
    setEditFormData({
      location_name: location.location_name || '',
      address: location.address || '',
      latitude: location.latitude ? location.latitude.toString() : '',
      longitude: location.longitude ? location.longitude.toString() : '',
      contact_person: location.contact_person || '',
      contact_phone: location.contact_phone || '',
      notes: location.notes || '',
      price_one_way: location.price_one_way ? location.price_one_way.toString() : '',
      price_two_way: location.price_two_way ? location.price_two_way.toString() : '',
      currency: location.currency || 'USD',
      is_active: location.is_active !== undefined ? location.is_active : true
    });
    setEditErrors({});
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (editErrors[name]) {
      setEditErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateEditForm = () => {
    const newErrors = {};
    
    if (!editFormData.location_name.trim()) {
      newErrors.location_name = 'Location name is required';
    }
    
    if (!editFormData.address.trim()) {
      newErrors.address = 'Address is required';
    }
    
    // Validate latitude if provided
    if (editFormData.latitude && (isNaN(editFormData.latitude) || parseFloat(editFormData.latitude) < -90 || parseFloat(editFormData.latitude) > 90)) {
      newErrors.latitude = 'Latitude must be a number between -90 and 90';
    }
    
    // Validate longitude if provided
    if (editFormData.longitude && (isNaN(editFormData.longitude) || parseFloat(editFormData.longitude) < -180 || parseFloat(editFormData.longitude) > 180)) {
      newErrors.longitude = 'Longitude must be a number between -180 and 180';
    }
    
    // Validate phone number if provided
    if (editFormData.contact_phone && !/^[\d\s\-\+\(\)]+$/.test(editFormData.contact_phone)) {
      newErrors.contact_phone = 'Please enter a valid phone number';
    }
    
    // Validate prices if provided
    if (editFormData.price_one_way && (isNaN(editFormData.price_one_way) || parseFloat(editFormData.price_one_way) < 0)) {
      newErrors.price_one_way = 'Price must be a positive number';
    }
    
    if (editFormData.price_two_way && (isNaN(editFormData.price_two_way) || parseFloat(editFormData.price_two_way) < 0)) {
      newErrors.price_two_way = 'Price must be a positive number';
    }
    
    setEditErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdateLocation = async (e) => {
    e.preventDefault();
    
    if (!validateEditForm()) {
      return;
    }
    
    setIsUpdating(true);
    
    try {
      const updateData = {
        location_name: editFormData.location_name.trim(),
        address: editFormData.address.trim(),
        latitude: editFormData.latitude ? parseFloat(editFormData.latitude) : null,
        longitude: editFormData.longitude ? parseFloat(editFormData.longitude) : null,
        contact_person: editFormData.contact_person.trim() || null,
        contact_phone: editFormData.contact_phone.trim() || null,
        notes: editFormData.notes.trim() || null,
        price_one_way: editFormData.price_one_way ? parseFloat(editFormData.price_one_way) : null,
        price_two_way: editFormData.price_two_way ? parseFloat(editFormData.price_two_way) : null,
        currency: editFormData.currency || 'USD',
        is_active: editFormData.is_active,
        updated_at: new Date().toISOString()
      };
      
      let updateQuery = supabase
        .from('pickup_locations')
        .update(updateData)
        .eq('id', editingLocation.id);

      if (selectedBranchId != null) {
        updateQuery = updateQuery.eq('branch_id', selectedBranchId);
      }

      const { error } = await updateQuery;
      
      if (error) {
        console.error('Error updating pickup location:', error);
        alert('Error updating pickup location: ' + error.message);
        return;
      }
      
      alert('Pickup location updated successfully!');
      setEditingLocation(null);
      fetchPickupLocations();
    } catch (err) {
      console.error('Error:', err);
      alert('An error occurred while updating the pickup location. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteLocation = async (location) => {
    if (!window.confirm(`Are you sure you want to delete "${location.location_name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      let deleteQuery = supabase
        .from('pickup_locations')
        .delete()
        .eq('id', location.id);

      if (selectedBranchId != null) {
        deleteQuery = deleteQuery.eq('branch_id', selectedBranchId);
      }

      const { error } = await deleteQuery;

      if (error) {
        console.error('Error deleting pickup location:', error);
        alert('Error deleting pickup location: ' + error.message);
        return;
      }

      // Refresh the list
      fetchPickupLocations();
      alert('Pickup location deleted successfully!');
    } catch (err) {
      console.error('Error:', err);
      alert('An error occurred while deleting the pickup location.');
    }
  };

  const formatCurrency = (amount, currency) => {
    if (!amount) return '-';
    const currencySymbols = {
      'USD': '$',
      'PKR': '₨',
      'EUR': '€',
      'GBP': '£',
      'INR': '₹',
      'AED': 'د.إ',
      'SAR': '﷼',
      'CAD': 'C$',
      'AUD': 'A$',
      'JPY': '¥',
      'CNY': '¥'
    };
    const symbol = currencySymbols[currency] || currency || '$';
    return `${symbol}${parseFloat(amount).toFixed(2)}`;
  };

  if (loading) {
    return (
      <div className="school-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
        handleLogout={handleLogout} 
      />
      
      {/* Sidebar */}
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />
      <div className="main-content">
        <div className="pickup-locations-page-container">
          <div className="pickup-locations-container">
            {/* Page Header Card */}
            <div className="pickup-locations-header-card">
              <div className="pickup-locations-header-icon">📍</div>
              <h1 className="pickup-locations-header-title">All Pickup Locations</h1>
              <div className="pickup-locations-header-subtitle">
                <span>Total Locations:</span>
                <span className="pickup-locations-count-badge">{pickupLocations.length}</span>
              </div>
              <div className="pickup-locations-add-button-container">
                <button
                  className="pickup-locations-add-button"
                  onClick={() => navigate('/school/add-pickup-location')}
                >
                  <span className="pickup-locations-add-button-icon">➕</span>
                  Add Location
                </button>
              </div>
            </div>

            {loadingLocations ? (
              <div className="loading-container" style={{ padding: '2rem' }}>
                <div className="loading-spinner"></div>
                <p>Loading pickup locations...</p>
              </div>
            ) : pickupLocations.length === 0 ? (
              <div className="pickup-locations-empty-state">
                <div className="pickup-locations-empty-icon">📍</div>
                <p className="pickup-locations-empty-message">No pickup locations found</p>
                <button
                  className="pickup-locations-empty-button"
                  onClick={() => navigate('/school/add-pickup-location')}
                >
                  Add Your First Pickup Location
                </button>
              </div>
            ) : (
              <>
                {/* Desktop Table */}
                <div className="pickup-locations-table-container">
                  <table className="pickup-locations-table">
                    <thead>
                      <tr>
                        <th className="location-name-header">Location Name</th>
                        <th className="address-header">Address</th>
                        <th className="coordinates-header">Coordinates</th>
                        <th className="contact-person-header">Contact Person</th>
                        <th className="contact-phone-header">Contact Phone</th>
                        <th className="status-header">Status</th>
                        <th className="added-date-header">Added Date</th>
                        <th className="actions-header">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pickupLocations.map((location) => (
                        <tr key={location.id}>
                          <td>
                            <div className="location-name-cell">
                              <span className="location-name-icon">📍</span>
                              <span>{location.location_name || '-'}</span>
                            </div>
                          </td>
                          <td>
                            <div className="address-cell">
                              <span className="address-icon">🏠</span>
                              <span>{location.address || '-'}</span>
                            </div>
                          </td>
                          <td className="coordinates-cell">
                            {location.latitude && location.longitude ? (
                              <span className="coordinates-badge">
                                {parseFloat(location.latitude).toFixed(6)}, {parseFloat(location.longitude).toFixed(6)}
                              </span>
                            ) : (
                              <span className="coordinates-badge empty">-</span>
                            )}
                          </td>
                          <td>
                            {location.contact_person ? (
                              <div className="contact-person-cell">
                                <span className="contact-person-icon">👤</span>
                                <span>{location.contact_person}</span>
                              </div>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td className="contact-phone-cell">
                            {location.contact_phone ? (
                              <>
                                <span className="contact-phone-icon">📞</span>
                                <span>{location.contact_phone}</span>
                              </>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td className="status-cell" style={{ textAlign: 'center' }}>
                            <span className={`status-badge ${location.is_active ? 'active' : 'inactive'}`}>
                              {location.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td className="added-date-cell">
                            <span className="added-date-icon">📅</span>
                            <span>{formatDate(location.created_at)}</span>
                          </td>
                          <td className="actions-cell">
                            <div className="pickup-locations-actions-container">
                              <button
                                className="location-action-btn view-btn"
                                onClick={() => handleViewLocation(location)}
                                title="View Details"
                              >
                                👁️
                              </button>
                              <button
                                className="location-action-btn edit-btn"
                                onClick={() => handleEditLocation(location)}
                                title="Edit Location"
                              >
                                ✏️
                              </button>
                              <button
                                className="location-action-btn delete-btn"
                                onClick={() => handleDeleteLocation(location)}
                                title="Delete Location"
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards */}
                <div className="pickup-locations-mobile-cards">
                  {pickupLocations.map((location) => (
                    <div key={location.id} className="pickup-location-mobile-card">
                      <div className="pickup-location-mobile-card-header">
                        <div className="pickup-location-mobile-name">
                          <span>📍</span>
                          <span>{location.location_name || '-'}</span>
                        </div>
                        <span className={`pickup-location-mobile-status-badge ${location.is_active ? 'active' : 'inactive'}`}>
                          {location.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="pickup-location-mobile-details">
                        <div className="pickup-location-mobile-detail-row">
                          <span className="pickup-location-mobile-detail-label">Address</span>
                          <span className="pickup-location-mobile-detail-value">
                            <span>🏠</span>
                            <span>{location.address || '-'}</span>
                          </span>
                        </div>
                        <div className="pickup-location-mobile-detail-row">
                          <span className="pickup-location-mobile-detail-label">Coordinates</span>
                          <span className="pickup-location-mobile-detail-value">
                            {location.latitude && location.longitude ? (
                              <span className="coordinates-badge">
                                {parseFloat(location.latitude).toFixed(6)}, {parseFloat(location.longitude).toFixed(6)}
                              </span>
                            ) : (
                              <span className="coordinates-badge empty">-</span>
                            )}
                          </span>
                        </div>
                        {location.contact_person && (
                          <div className="pickup-location-mobile-detail-row">
                            <span className="pickup-location-mobile-detail-label">Contact Person</span>
                            <span className="pickup-location-mobile-detail-value">
                              <span>👤</span>
                              <span>{location.contact_person}</span>
                            </span>
                          </div>
                        )}
                        {location.contact_phone && (
                          <div className="pickup-location-mobile-detail-row">
                            <span className="pickup-location-mobile-detail-label">Contact Phone</span>
                            <span className="pickup-location-mobile-detail-value">
                              <span>📞</span>
                              <span>{location.contact_phone}</span>
                            </span>
                          </div>
                        )}
                        <div className="pickup-location-mobile-detail-row">
                          <span className="pickup-location-mobile-detail-label">Added Date</span>
                          <span className="pickup-location-mobile-detail-value">
                            <span>📅</span>
                            <span>{formatDate(location.created_at)}</span>
                          </span>
                        </div>
                      </div>
                      <div className="pickup-location-mobile-actions">
                        <button
                          className="pickup-location-mobile-action-btn view-btn"
                          onClick={() => handleViewLocation(location)}
                        >
                          <span>👁️</span>
                          <span>View</span>
                        </button>
                        <button
                          className="pickup-location-mobile-action-btn edit-btn"
                          onClick={() => handleEditLocation(location)}
                        >
                          <span>✏️</span>
                          <span>Edit</span>
                        </button>
                        <button
                          className="pickup-location-mobile-action-btn delete-btn"
                          onClick={() => handleDeleteLocation(location)}
                        >
                          <span>🗑️</span>
                          <span>Delete</span>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {/* View Location Modal */}
            {viewingLocation && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
              }}
              onClick={() => setViewingLocation(null)}
              >
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '2rem',
                  maxWidth: '600px',
                  width: '90%',
                  maxHeight: '80vh',
                  overflowY: 'auto',
                  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                }}
                onClick={(e) => e.stopPropagation()}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1.5rem'
                  }}>
                    <h2 style={{ margin: 0, color: '#1F2937' }}>Location Details</h2>
                    <button
                      onClick={() => setViewingLocation(null)}
                      style={{
                        background: 'transparent',
                        border: 'none',
                        fontSize: '1.5rem',
                        cursor: 'pointer',
                        color: '#6B7280'
                      }}
                    >
                      ×
                    </button>
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div>
                      <strong style={{ color: '#6B7280', fontSize: '0.875rem' }}>Location Name:</strong>
                      <p style={{ margin: '0.25rem 0 0 0', fontSize: '1.1rem', fontWeight: '600' }}>
                        {viewingLocation.location_name}
                      </p>
                    </div>
                    
                    <div>
                      <strong style={{ color: '#6B7280', fontSize: '0.875rem' }}>Address:</strong>
                      <p style={{ margin: '0.25rem 0 0 0' }}>{viewingLocation.address}</p>
                    </div>
                    
                    {viewingLocation.latitude && viewingLocation.longitude && (
                      <div>
                        <strong style={{ color: '#6B7280', fontSize: '0.875rem' }}>Coordinates:</strong>
                        <p style={{ margin: '0.25rem 0 0 0' }}>
                          {parseFloat(viewingLocation.latitude).toFixed(6)}, {parseFloat(viewingLocation.longitude).toFixed(6)}
                        </p>
                      </div>
                    )}
                    
                    {viewingLocation.contact_person && (
                      <div>
                        <strong style={{ color: '#6B7280', fontSize: '0.875rem' }}>Contact Person:</strong>
                        <p style={{ margin: '0.25rem 0 0 0' }}>{viewingLocation.contact_person}</p>
                      </div>
                    )}
                    
                    {viewingLocation.contact_phone && (
                      <div>
                        <strong style={{ color: '#6B7280', fontSize: '0.875rem' }}>Contact Phone:</strong>
                        <p style={{ margin: '0.25rem 0 0 0' }}>{viewingLocation.contact_phone}</p>
                      </div>
                    )}
                    
                    {(viewingLocation.price_one_way || viewingLocation.price_two_way) && (
                      <div>
                        <strong style={{ color: '#6B7280', fontSize: '0.875rem' }}>Pricing:</strong>
                        <div style={{ marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                          {viewingLocation.price_one_way && (
                            <p style={{ margin: 0 }}>
                              <strong>1 Way:</strong> {formatCurrency(viewingLocation.price_one_way, viewingLocation.currency)}
                            </p>
                          )}
                          {viewingLocation.price_two_way && (
                            <p style={{ margin: 0 }}>
                              <strong>2 Way:</strong> {formatCurrency(viewingLocation.price_two_way, viewingLocation.currency)}
                            </p>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {viewingLocation.notes && (
                      <div>
                        <strong style={{ color: '#6B7280', fontSize: '0.875rem' }}>Notes:</strong>
                        <p style={{ margin: '0.25rem 0 0 0' }}>{viewingLocation.notes}</p>
                      </div>
                    )}
                    
                    <div>
                      <strong style={{ color: '#6B7280', fontSize: '0.875rem' }}>Status:</strong>
                      <span
                        style={{
                          display: 'inline-block',
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.85rem',
                          fontWeight: '500',
                          backgroundColor: viewingLocation.is_active ? '#D1FAE5' : '#FEE2E2',
                          color: viewingLocation.is_active ? '#065F46' : '#991B1B',
                          marginLeft: '0.5rem'
                        }}
                      >
                        {viewingLocation.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                    
                    <div>
                      <strong style={{ color: '#6B7280', fontSize: '0.875rem' }}>Added Date:</strong>
                      <p style={{ margin: '0.25rem 0 0 0' }}>{formatDate(viewingLocation.created_at)}</p>
                    </div>
                  </div>
                  
                  <div style={{
                    marginTop: '1.5rem',
                    display: 'flex',
                    gap: '0.75rem',
                    justifyContent: 'flex-end'
                  }}>
                    <button
                      onClick={() => {
                        setViewingLocation(null);
                        handleEditLocation(viewingLocation);
                      }}
                      style={{
                        background: '#10B981',
                        color: 'white',
                        border: 'none',
                        padding: '0.5rem 1rem',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '500'
                      }}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => setViewingLocation(null)}
                      style={{
                        background: '#6B7280',
                        color: 'white',
                        border: 'none',
                        padding: '0.5rem 1rem',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '500'
                      }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            )}


            {/* Edit Location Modal */}
            {editingLocation && (
              <div style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
              }}
              onClick={() => setEditingLocation(null)}
              >
                <div style={{
                  background: 'white',
                  borderRadius: '12px',
                  padding: '2rem',
                  maxWidth: '800px',
                  width: '90%',
                  maxHeight: '90vh',
                  overflowY: 'auto',
                  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                }}
                onClick={(e) => e.stopPropagation()}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1.5rem',
                    paddingBottom: '1rem',
                    borderBottom: '2px solid #E5E7EB'
                  }}>
                    <h2 style={{ margin: 0, color: '#1F2937' }}>Edit Pickup Location</h2>
                    <button
                      onClick={() => setEditingLocation(null)}
                      style={{
                        background: '#F3F4F6',
                        border: 'none',
                        borderRadius: '50%',
                        width: '32px',
                        height: '32px',
                        fontSize: '1.5rem',
                        cursor: 'pointer',
                        color: '#6B7280',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: 1
                      }}
                    >
                      ×
                    </button>
                  </div>
                  
                  <form onSubmit={handleUpdateLocation}>
                    <div style={{ marginBottom: '2rem' }}>
                      <h3 style={{ marginBottom: '1rem', color: '#374151', fontSize: '1.1rem' }}>Pickup Location Information</h3>
                      
                      <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                          Location Name <span style={{ color: '#EF4444' }}>*</span>
                        </label>
                        <input
                          type="text"
                          name="location_name"
                          value={editFormData.location_name}
                          onChange={handleEditInputChange}
                          placeholder="Enter location name"
                          required
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: editErrors.location_name ? '2px solid #EF4444' : '1px solid #D1D5DB',
                            borderRadius: '8px',
                            fontSize: '0.95rem'
                          }}
                        />
                        {editErrors.location_name && (
                          <span style={{ color: '#EF4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                            {editErrors.location_name}
                          </span>
                        )}
                      </div>
                      
                      <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                          Address <span style={{ color: '#EF4444' }}>*</span>
                        </label>
                        <textarea
                          name="address"
                          value={editFormData.address}
                          onChange={handleEditInputChange}
                          placeholder="Enter full address"
                          rows="3"
                          required
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: editErrors.address ? '2px solid #EF4444' : '1px solid #D1D5DB',
                            borderRadius: '8px',
                            fontSize: '0.95rem',
                            resize: 'vertical'
                          }}
                        />
                        {editErrors.address && (
                          <span style={{ color: '#EF4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                            {editErrors.address}
                          </span>
                        )}
                      </div>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                            Latitude (Optional)
                          </label>
                          <input
                            type="number"
                            name="latitude"
                            value={editFormData.latitude}
                            onChange={handleEditInputChange}
                            placeholder="e.g., 40.7128"
                            step="any"
                            min="-90"
                            max="90"
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              border: editErrors.latitude ? '2px solid #EF4444' : '1px solid #D1D5DB',
                              borderRadius: '8px',
                              fontSize: '0.95rem'
                            }}
                          />
                          {editErrors.latitude && (
                            <span style={{ color: '#EF4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                              {editErrors.latitude}
                            </span>
                          )}
                        </div>
                        
                        <div>
                          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                            Longitude (Optional)
                          </label>
                          <input
                            type="number"
                            name="longitude"
                            value={editFormData.longitude}
                            onChange={handleEditInputChange}
                            placeholder="e.g., -74.0060"
                            step="any"
                            min="-180"
                            max="180"
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              border: editErrors.longitude ? '2px solid #EF4444' : '1px solid #D1D5DB',
                              borderRadius: '8px',
                              fontSize: '0.95rem'
                            }}
                          />
                          {editErrors.longitude && (
                            <span style={{ color: '#EF4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                              {editErrors.longitude}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                            Contact Person (Optional)
                          </label>
                          <input
                            type="text"
                            name="contact_person"
                            value={editFormData.contact_person}
                            onChange={handleEditInputChange}
                            placeholder="Name of contact person"
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              border: '1px solid #D1D5DB',
                              borderRadius: '8px',
                              fontSize: '0.95rem'
                            }}
                          />
                        </div>
                        
                        <div>
                          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                            Contact Phone (Optional)
                          </label>
                          <input
                            type="text"
                            name="contact_phone"
                            value={editFormData.contact_phone}
                            onChange={handleEditInputChange}
                            placeholder="Phone number"
                            style={{
                              width: '100%',
                              padding: '0.75rem',
                              border: editErrors.contact_phone ? '2px solid #EF4444' : '1px solid #D1D5DB',
                              borderRadius: '8px',
                              fontSize: '0.95rem'
                            }}
                          />
                          {editErrors.contact_phone && (
                            <span style={{ color: '#EF4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                              {editErrors.contact_phone}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                          Additional Notes (Optional)
                        </label>
                        <textarea
                          name="notes"
                          value={editFormData.notes}
                          onChange={handleEditInputChange}
                          placeholder="Enter any additional notes"
                          rows="4"
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '1px solid #D1D5DB',
                            borderRadius: '8px',
                            fontSize: '0.95rem',
                            resize: 'vertical'
                          }}
                        />
                      </div>
                    </div>

                    <div style={{ marginBottom: '2rem', paddingTop: '1.5rem', borderTop: '2px solid #E5E7EB' }}>
                      <h3 style={{ marginBottom: '1rem', color: '#374151', fontSize: '1.1rem' }}>Pricing Information</h3>
                      
                      <div style={{ marginBottom: '1rem' }}>
                        <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                          Currency <span style={{ color: '#EF4444' }}>*</span>
                        </label>
                        <select
                          name="currency"
                          value={editFormData.currency}
                          onChange={handleEditInputChange}
                          style={{
                            width: '100%',
                            padding: '0.75rem',
                            border: '1px solid #D1D5DB',
                            borderRadius: '8px',
                            fontSize: '0.95rem',
                            backgroundColor: '#FFFFFF'
                          }}
                        >
                          <option value="USD">USD - US Dollar ($)</option>
                          <option value="PKR">PKR - Pakistani Rupee (₨)</option>
                          <option value="EUR">EUR - Euro (€)</option>
                          <option value="GBP">GBP - British Pound (£)</option>
                          <option value="INR">INR - Indian Rupee (₹)</option>
                          <option value="AED">AED - UAE Dirham (د.إ)</option>
                          <option value="SAR">SAR - Saudi Riyal (﷼)</option>
                          <option value="CAD">CAD - Canadian Dollar (C$)</option>
                          <option value="AUD">AUD - Australian Dollar (A$)</option>
                          <option value="JPY">JPY - Japanese Yen (¥)</option>
                          <option value="CNY">CNY - Chinese Yuan (¥)</option>
                        </select>
                      </div>
                      
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                        <div>
                          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                            Price for 1 Way (Optional)
                          </label>
                          <div style={{ position: 'relative' }}>
                            <span style={{
                              position: 'absolute',
                              left: '0.75rem',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              color: '#6B7280',
                              fontSize: '0.95rem'
                            }}>
                              {editFormData.currency === 'USD' && '$'}
                              {editFormData.currency === 'PKR' && '₨'}
                              {editFormData.currency === 'EUR' && '€'}
                              {editFormData.currency === 'GBP' && '£'}
                              {editFormData.currency === 'INR' && '₹'}
                              {editFormData.currency === 'AED' && 'د.إ'}
                              {editFormData.currency === 'SAR' && '﷼'}
                              {editFormData.currency === 'CAD' && 'C$'}
                              {editFormData.currency === 'AUD' && 'A$'}
                              {editFormData.currency === 'JPY' && '¥'}
                              {editFormData.currency === 'CNY' && '¥'}
                            </span>
                            <input
                              type="number"
                              name="price_one_way"
                              value={editFormData.price_one_way}
                              onChange={handleEditInputChange}
                              placeholder="0.00"
                              step="0.01"
                              min="0"
                              style={{
                                width: '100%',
                                padding: '0.75rem',
                                paddingLeft: editFormData.currency === 'USD' ? '2rem' : '2.5rem',
                                border: editErrors.price_one_way ? '2px solid #EF4444' : '1px solid #D1D5DB',
                                borderRadius: '8px',
                                fontSize: '0.95rem'
                              }}
                            />
                          </div>
                          {editErrors.price_one_way && (
                            <span style={{ color: '#EF4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                              {editErrors.price_one_way}
                            </span>
                          )}
                        </div>
                        
                        <div>
                          <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151' }}>
                            Price for 2 Way (Optional)
                          </label>
                          <div style={{ position: 'relative' }}>
                            <span style={{
                              position: 'absolute',
                              left: '0.75rem',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              color: '#6B7280',
                              fontSize: '0.95rem'
                            }}>
                              {editFormData.currency === 'USD' && '$'}
                              {editFormData.currency === 'PKR' && '₨'}
                              {editFormData.currency === 'EUR' && '€'}
                              {editFormData.currency === 'GBP' && '£'}
                              {editFormData.currency === 'INR' && '₹'}
                              {editFormData.currency === 'AED' && 'د.إ'}
                              {editFormData.currency === 'SAR' && '﷼'}
                              {editFormData.currency === 'CAD' && 'C$'}
                              {editFormData.currency === 'AUD' && 'A$'}
                              {editFormData.currency === 'JPY' && '¥'}
                              {editFormData.currency === 'CNY' && '¥'}
                            </span>
                            <input
                              type="number"
                              name="price_two_way"
                              value={editFormData.price_two_way}
                              onChange={handleEditInputChange}
                              placeholder="0.00"
                              step="0.01"
                              min="0"
                              style={{
                                width: '100%',
                                padding: '0.75rem',
                                paddingLeft: editFormData.currency === 'USD' ? '2rem' : '2.5rem',
                                border: editErrors.price_two_way ? '2px solid #EF4444' : '1px solid #D1D5DB',
                                borderRadius: '8px',
                                fontSize: '0.95rem'
                              }}
                            />
                          </div>
                          {editErrors.price_two_way && (
                            <span style={{ color: '#EF4444', fontSize: '0.875rem', marginTop: '0.25rem', display: 'block' }}>
                              {editErrors.price_two_way}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          name="is_active"
                          checked={editFormData.is_active}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                          style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                        />
                        <span style={{ fontWeight: '600', color: '#374151' }}>Active</span>
                      </label>
                    </div>
                    
                    <div style={{
                      marginTop: '1.5rem',
                      display: 'flex',
                      gap: '0.75rem',
                      justifyContent: 'flex-end'
                    }}>
                      <button
                        type="button"
                        onClick={() => setEditingLocation(null)}
                        style={{
                          background: '#F3F4F6',
                          color: '#374151',
                          border: 'none',
                          padding: '0.75rem 1.5rem',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontWeight: '500',
                          fontSize: '0.95rem'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={isUpdating}
                        style={{
                          background: isUpdating ? '#9CA3AF' : '#FF8C42',
                          color: 'white',
                          border: 'none',
                          padding: '0.75rem 1.5rem',
                          borderRadius: '8px',
                          cursor: isUpdating ? 'not-allowed' : 'pointer',
                          fontWeight: '500',
                          fontSize: '0.95rem'
                        }}
                      >
                        {isUpdating ? 'Updating...' : 'Update Location'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PickupLocations;

